import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import configuration from "react-global-configuration";
import {
  isExternalHref,
  normalizeExternalUrl,
} from "../Helper/urls";
import { fetchShopProduct, formatShopPrice } from "./shopApi";
import { addCartItem } from "../Commerce/commerceApi";
import ShopProductGallery from "./ShopProductGallery";
import "./ShopProductDetail.css";

/**
 * A-006.2 / A-006.3 — Public product detail under /shop/product/:slug
 * Compatible with react-router-dom 4.3.1 (props.match.params).
 * A-008 — Add to cart delegates to Commerce Engine (cartPayload only).
 */
function ShopProductDetail(props) {
  const slug =
    (props.match && props.match.params && props.match.params.slug) || "";
  const siteName = configuration.get("configData.site_name") || "Shop";
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [adding, setAdding] = useState(false);
  const [cartMessage, setCartMessage] = useState("");
  const [cartError, setCartError] = useState("");

  useEffect(() => {
    let cancelled = false;
    if (!slug) {
      setLoading(false);
      setNotFound(true);
      setProduct(null);
      return undefined;
    }
    (async () => {
      setLoading(true);
      setNotFound(false);
      try {
        const data = await fetchShopProduct({ slug });
        if (cancelled) return;
        if (!data) {
          setNotFound(true);
          setProduct(null);
        } else {
          setProduct(data);
        }
      } catch (e) {
        if (!cancelled) {
          setNotFound(true);
          setProduct(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (loading) {
    return (
      <div className="shop-product-detail shop-product-detail--loading">
        <p>Loading…</p>
      </div>
    );
  }

  if (notFound || !product) {
    return (
      <div className="shop-product-detail shop-product-detail--empty">
        <h1>Product not found</h1>
        <p>This product is unavailable or no longer listed.</p>
        <Link to="/shop" className="btn btn-danger">
          Back to Shop
        </Link>
      </div>
    );
  }

  const seo = product.seo || {};
  const title = seo.title || product.name;
  const description = seo.description || product.short_description || "";
  const ogImage = seo.og_image || product.primary_image;
  const priceLabel = product.is_free
    ? "Free"
    : formatShopPrice(product.effective_price);

  const galleryImages = [];
  if (product.primary_image) {
    galleryImages.push({
      id: "primary",
      path: product.primary_image,
      alt_text: product.name,
    });
  }
  if (product.media && product.media.length) {
    product.media
      .filter((m) => m.media_type === "image" && m.path)
      .slice()
      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
      .forEach((m) => {
        if (product.primary_image && m.path === product.primary_image) {
          return;
        }
        galleryImages.push(m);
      });
  }

  return (
    <div className="shop-product-detail">
      <Helmet>
        <title>{`${title} | ${siteName}`}</title>
        {description ? <meta name="description" content={description} /> : null}
        <meta property="og:title" content={title} />
        {description ? (
          <meta property="og:description" content={description} />
        ) : null}
        {ogImage ? <meta property="og:image" content={ogImage} /> : null}
      </Helmet>

      <div className="shop-product-detail__inner">
        <p className="shop-product-detail__crumb">
          <Link to="/shop">Shop</Link>
          <span aria-hidden="true"> / </span>
          <span>{product.name}</span>
        </p>

        <div className="shop-product-detail__layout">
          <ShopProductGallery
            images={galleryImages}
            productName={product.name}
          />

          <div className="shop-product-detail__info">
            <h1 className="shop-product-detail__title">{product.name}</h1>
            {priceLabel ? (
              <p className="shop-product-detail__price">
                {product.is_on_sale &&
                product.regular_price != null &&
                !product.is_free ? (
                  <span className="shop-product-detail__price-was">
                    {formatShopPrice(product.regular_price)}
                  </span>
                ) : null}
                <span>{priceLabel}</span>
              </p>
            ) : null}
            {product.short_description ? (
              <p className="shop-product-detail__short">
                {product.short_description}
              </p>
            ) : null}
            {product.description ? (
              <div className="shop-product-detail__description">
                {product.description.split("\n").map((line, i) => (
                  <p key={i}>{line}</p>
                ))}
              </div>
            ) : null}

            {product.attributes && product.attributes.length ? (
              <dl className="shop-product-detail__attrs">
                {product.attributes.map((attr, i) => (
                  <div key={i} className="shop-product-detail__attr">
                    <dt>{attr.name}</dt>
                    <dd>{attr.value}</dd>
                  </div>
                ))}
              </dl>
            ) : null}

            {product.product_type === "external_link" && product.external_url ? (
              <a
                className="btn btn-danger shop-product-detail__cta"
                href={
                  isExternalHref(product.external_url)
                    ? normalizeExternalUrl(product.external_url)
                    : product.external_url
                }
                target="_blank"
                rel="noopener noreferrer"
              >
                View product
              </a>
            ) : product.stock_status === "out_of_stock" ? (
              <p className="shop-product-detail__note">Out of stock</p>
            ) : (
              <div className="shop-product-detail__cart-actions">
                <button
                  type="button"
                  className="btn btn-danger shop-product-detail__cta"
                  disabled={adding}
                  onClick={async () => {
                    setAdding(true);
                    setCartError("");
                    setCartMessage("");
                    try {
                      await addCartItem({
                        purchasableType: "shop_product",
                        purchasableId: product.id,
                        quantity: 1,
                      });
                      setCartMessage("Added to cart.");
                    } catch (e) {
                      setCartError(e.message || "Could not add to cart.");
                    } finally {
                      setAdding(false);
                    }
                  }}
                >
                  {adding ? "Adding…" : "Add to cart"}
                </button>
                <Link className="shop-product-detail__cart-link" to="/cart">
                  View cart
                </Link>
                {cartMessage ? (
                  <p className="shop-product-detail__cart-ok">{cartMessage}</p>
                ) : null}
                {cartError ? (
                  <p className="shop-product-detail__cart-err">{cartError}</p>
                ) : null}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ShopProductDetail;
